import { Inject, Injectable, Injector } from "@angular/core";
import { ZoneMetadata } from "@common/ADAPT.Common.Model/methodology/zone";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { TeamLocation } from "@common/ADAPT.Common.Model/organisation/team-location";
import { TeamTypeConfiguration } from "@common/ADAPT.Common.Model/organisation/team-type-configuration";
import { TEAM_CONFIGURATION_PAGE } from "@common/page-route-providers";
import { IAdaptRoute } from "@common/route/page-route-builder";
import { BaseService } from "@common/service/base.service";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { IConfirmationDialogData } from "@common/ux/adapt-common-dialog/confirmation-dialog.component/confirmation-dialog.component";
import { EditSimpleValueBreezeEntityDialogComponent, IEditSimpleValueBreezeEntityDialogData, SimpleValueType } from "@common/ux/edit-simple-value-breeze-entity-dialog/edit-simple-value-breeze-entity-dialog.component";
import { ActivityFeedDialogComponent } from "@org-common/lib/activity-feed/activity-feed-dialog/activity-feed-dialog.component";
import { catchError, delay, of, switchMap } from "rxjs";
import { ChangeTeamAppearanceDialogComponent } from "./change-team-appearance-dialog/change-team-appearance-dialog.component";
import { CommonTeamsService } from "./common-teams.service";
import { ConfigureTeamStewardshipDialogComponent } from "./configure-team-stewardship-dialog/configure-team-stewardship-dialog.component";
import { EditTeamLocationsDialogComponent } from "./edit-team-locations-dialog/edit-team-locations-dialog.component";

@Injectable({
    providedIn: "root",
})
export class CommonTeamsUiService extends BaseService {
    public constructor(
        injector: Injector,
        private commonDialogService: AdaptCommonDialogService,
        private teamsService: CommonTeamsService,
        @Inject(TEAM_CONFIGURATION_PAGE) private teamConfigurationPageRoute: IAdaptRoute<{ teamId: number }>,
    ) {
        super(injector);
    }

    public reactivateTeam(team: Team, redirect = true) {
        const dialogData: IConfirmationDialogData = {
            title: "Reactivate group",
            message: "Reactivating the group will make it visible to everyone in the organisation.<br /><br /><b>After reactivating the group, update any required data on the group's configuration page (such as the people, action boards, active features).</b>",
            checkboxMessage: "Confirm that you wish to reactivate this group",
        };

        return this.commonDialogService.openConfirmationDialog(dialogData).pipe(
            switchMap(() => this.teamsService.reactivateTeam(team)),
            delay(50), // force a delay to allow the route change to function without hanging the browser (unknown cause)
            switchMap(() => redirect
                ? this.teamConfigurationPageRoute.gotoRoute({ teamId: team.teamId })
                : of(null)),
        );
    }

    public viewActivityFeed(team: Team) {
        return this.commonDialogService.open(ActivityFeedDialogComponent, team);
    }

    public configureTeamStewardship(team: Team) {
        return this.commonDialogService.open(ConfigureTeamStewardshipDialogComponent, team);
    }

    public configureTeamAccountabilities(team: Team) {
        return this.teamsService.getOrCreateTeamSupplementaryData(team).pipe(
            switchMap((suppData) => this.commonDialogService.open(EditSimpleValueBreezeEntityDialogComponent, {
                title: `Edit accountabilities for ${team.name}`,
                entities: [{
                    entity: suppData,
                    fieldName: "accountabilities",
                    type: SimpleValueType.RichText,
                }],
                saveOnClose: true,
            } as IEditSimpleValueBreezeEntityDialogData))
        );
    }

    public deleteTeamLocation(teamLocation: TeamLocation) {
        let message = `<p>Are you sure you want to remove the <b>${teamLocation.team?.name}</b> ${teamLocation.team?.teamTypeName.toLocaleLowerCase()} from `;
        if (teamLocation.parentTeam) {
            message += `within the <b>${teamLocation.parentTeam.name}</b> ${teamLocation.parentTeam.teamTypeName.toLocaleLowerCase()}?`;
        } else if (teamLocation.zone) {
            message += `within the <b>${ZoneMetadata.Name[teamLocation.zone]}</b> zone?`;
        } else {
            message += `this location?`;
        }

        message += `</p><p>The <b>${teamLocation.team?.name}</b> ${teamLocation.team?.teamTypeName.toLocaleLowerCase()} itself will not be deleted. It can be added to this location again at any time.</p>`;

        if (teamLocation.team?.teamLocations.length === 1) {
            message += `<p>This team has no other locations defined, so it will appear under <b>Global Teams</b> after this location is deleted.</p>`;
        }

        return this.commonDialogService.openConfirmationDialog({
            title: "Delete team location",
            message,
            confirmButtonPreset: "deleteAndSave",
        }).pipe(
            switchMap(() => this.commonDataService.remove(teamLocation)),
            switchMap(() => this.commonDataService.saveEntities([teamLocation])),
            catchError(() => this.commonDialogService.handleSaveFailure([teamLocation])),
        );
    }

    public editTeamLocations(team: Team) {
        return this.commonDialogService.open(EditTeamLocationsDialogComponent, team);
    }

    public changeTeamAppearance(entity: Team | TeamTypeConfiguration) {
        return this.commonDialogService.open(ChangeTeamAppearanceDialogComponent, entity);
    }
}
